import { LoginForm } from '../components/loginForm';
import { AuthService } from './authService';
import { RedirectService } from './redirectService';
import { getRedirectResult, onIdTokenChanged, User } from 'firebase/auth';
import { getElement, getElements } from '../utils/getElement';
import { Logger } from '../utils/logger';
import { isTopApp } from '../utils/isTopApp';
import { MessageService } from './messageService';
import { ToastService } from './toastService';
import { htmlIds } from '../constants';

interface LoginFormData {
  email: string;
  password: string;
}

const SessionStatus = {
  Active: 'active',
  None: 'none',
  Unknown: 'unknown',
} as const;

type SessionStatus = (typeof SessionStatus)[keyof typeof SessionStatus];

export class UlaService {
  private static instance: UlaService;
  private readonly authService: AuthService;
  private readonly messageService: MessageService;
  private readonly redirectService: RedirectService;
  private readonly loginForm: LoginForm;
  private logoutInProgress: boolean = false;
  private bootstrapInFlight: Promise<SessionStatus> | null = null;

  private constructor() {
    this.authService = AuthService.getInstance();
    this.messageService = MessageService.getInstance();
    this.redirectService = RedirectService.getInstance();
    this.loginForm = new LoginForm();
  }

  public static getInstance(): UlaService {
    if (!UlaService.instance) {
      UlaService.instance = new UlaService();
    }
    return UlaService.instance;
  }

  public async initialize(): Promise<void> {
    try {
      await this.authService.initialized;
      this.messageService.startListening();

      if (isTopApp()) {
        await this.handleTopAppInit();
      } else {
        await this.handleIframeInit();
      }
    } catch (error) {
      Logger.logError('Failed to initialize application:', error);
      throw error;
    }
  }

  private setupLoginFormListeners(): void {
    if (!isTopApp()) return;

    const signInForm = getElement<HTMLFormElement>(`#${htmlIds.loginForm}`);
    const googleLogin = getElement<HTMLButtonElement>(`#${htmlIds.googleLogin}`);

    signInForm.addEventListener('submit', this.handleFormSubmit.bind(this));
    googleLogin.addEventListener('click', this.handleGoogleLogin.bind(this));

    const quickLoginButtons = getElements<HTMLFormElement>(`.quick-login-button`);

    for (const button of quickLoginButtons) {
      button.addEventListener('click', () => {
        console.log('clicked', button);
        const { email, password } = button.dataset;
        if (email && password) {
          this.loginForm.validateForm({
            email,
            password,
          });
          this.authService.loginWithPassword(email, password).catch((error) => {
            Logger.logError('Quick login failed:', error);
          });
        }
      });
    }

    this.setupDebugListeners();
  }

  /** Wires the local-only debug controls (toggle staging banner, preview error toast). */
  private setupDebugListeners(): void {
    if (!this.loginForm.isLocal) return;

    const toggleBannerBtn = document.getElementById(htmlIds.debugToggleBannerBtn);
    toggleBannerBtn?.addEventListener('click', () => {
      const existing = document.getElementById(htmlIds.stagingBanner);
      if (existing) {
        existing.remove();
        return;
      }
      document.body.insertAdjacentHTML('afterbegin', this.loginForm.getStagingBannerMarkup());
    });

    const errorToastBtn = document.getElementById(htmlIds.debugErrorToastBtn);
    errorToastBtn?.addEventListener('click', () => {
      ToastService.getInstance().showErrorToast('Firebase: Error (auth/invalid-credential).');
    });
  }

  private async handleFormSubmit(e: SubmitEvent): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData: LoginFormData = {
      email: form[this.loginForm.formInputIds.email].value,
      password: form[this.loginForm.formInputIds.password].value,
    };

    try {
      this.loginForm.validateForm(formData);
      await this.authService.loginWithPassword(formData.email, formData.password);
    } catch (error) {
      // Validation errors are already handled by the LoginForm class
      if (!(error instanceof Error && error.name === 'LoginFormError')) {
        throw error;
      }
    }
  }

  private async handleGoogleLogin(e: MouseEvent): Promise<void> {
    e.preventDefault();
    await this.authService.loginWithGoogle();
  }

  private consumeQuickLoginParam(): { email: string; password: string } | null {
    const url = new URL(globalThis.location.href);
    const encoded = url.searchParams.get('quickLogin');
    if (!encoded) return null;

    url.searchParams.delete('quickLogin');
    globalThis.history.replaceState({}, '', url.href);

    try {
      const decoded = atob(encoded);
      const colonIndex = decoded.indexOf(':');
      if (colonIndex === -1) throw new Error('missing colon separator');
      const email = decoded.slice(0, colonIndex);
      const password = decoded.slice(colonIndex + 1);
      if (!email || !password) throw new Error('empty email or password');
      return { email, password };
    } catch (error) {
      Logger.logError('Failed to decode quickLogin parameter:', error);
      return null;
    }
  }

  private async renderApp(): Promise<void> {
    const appContainer = getElement<HTMLDivElement>(`#${htmlIds.ulaApp}`);
    await this.loginForm.render(appContainer);
    this.setupLoginFormListeners();

    const credentials = this.consumeQuickLoginParam();
    if (credentials) {
      try {
        await this.authService.loginWithPassword(credentials.email, credentials.password);
      } catch (error) {
        Logger.logError('quickLogin auto-login failed:', error);
      }
    }
  }

  private async renderLoggedIn(): Promise<void> {
    const appContainer = getElement<HTMLDivElement>(`#${htmlIds.ulaApp}`);
    await this.loginForm.renderLoggedIn(appContainer, this.authService.loggedUser!);
    this.setupLoggedInListeners();
  }

  private setupLoggedInListeners(): void {
    const goToAppBtn = getElement<HTMLButtonElement>(`#${htmlIds.goToAppBtn}`);
    const logoutBtn = getElement<HTMLButtonElement>(`#${htmlIds.logoutBtn}`);

    goToAppBtn.addEventListener('click', () => {
      this.redirectService.redirectToApp();
    });

    logoutBtn.addEventListener('click', async () => {
      await this.authService.logout({ clearCookie: true });
      await this.renderApp();
    });

    this.setupImpersonationListeners();
    this.setupStopImpersonationListener();
    this.setupDebugListeners();
  }

  private setupStopImpersonationListener(): void {
    if (!this.authService.loggedUser?.isImpersonated) return;

    const stopBtn = getElement<HTMLButtonElement>(`#${htmlIds.stopImpersonationBtn}`);

    stopBtn.addEventListener('click', async () => {
      stopBtn.disabled = true;
      try {
        await this.authService.stopImpersonate();
        await this.renderLoggedIn();
      } catch (error) {
        Logger.logError('Stop impersonation failed:', error);
      } finally {
        stopBtn.disabled = false;
      }
    });
  }

  private setupImpersonationListeners(): void {
    const displayName = getElement<HTMLElement>(`#${htmlIds.userDisplayName}`);
    const dialog = getElement<HTMLDialogElement>(`#${htmlIds.impersonateDialog}`);
    const input = getElement<HTMLInputElement>(`#${htmlIds.impersonateInput}`);
    const confirmBtn = getElement<HTMLButtonElement>(`#${htmlIds.impersonateConfirmBtn}`);
    const cancelBtn = getElement<HTMLButtonElement>(`#${htmlIds.impersonateCancelBtn}`);

    displayName.addEventListener('dblclick', () => {
      input.value = '';
      dialog.showModal();
      input.focus();
    });

    cancelBtn.addEventListener('click', () => {
      dialog.close();
    });

    confirmBtn.addEventListener('click', async () => {
      const collaboratorUuid = input.value.trim();
      if (!collaboratorUuid) return;

      confirmBtn.disabled = true;
      try {
        await this.authService.impersonate(collaboratorUuid);
        dialog.close();
        await this.renderLoggedIn();
      } catch (error) {
        Logger.logError('Impersonation failed:', error);
      } finally {
        confirmBtn.disabled = false;
      }
    });
  }

  private consumeLogoutParam(): boolean {
    const url = new URL(globalThis.location.href);
    if (!url.searchParams.has('logout')) return false;
    url.searchParams.delete('logout');
    globalThis.history.replaceState({}, '', url.href);
    return true;
  }

  private async handleTopAppInit(): Promise<void> {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (this.consumeLogoutParam() && isSafari) {
      await this.authService.logout({ clearCookie: true });
      await this.renderApp();
      return;
    }

    const redirectResult = await getRedirectResult(this.authService.firebaseAuth);

    if (redirectResult) {
      try {
        await this.authService.handleLoginResponse(redirectResult);
      } catch (e: unknown) {
        Logger.logWarn('Failed to handle login response. Rendering app...', e);
        await this.renderApp();
      }
    } else {
      const currentUser = this.authService.firebaseAuth.currentUser;
      if (currentUser) {
        try {
          const idToken = await currentUser.getIdToken();
          await this.authService.refreshAGWLogin(idToken);

          // eslint-disable-next-line sonarjs/nested-control-flow
          if (this.authService.loggedUser) {
            await this.renderLoggedIn();
            return;
          }
        } catch (e: unknown) {
          Logger.logWarn('Failed to verify existing session. Rendering login form...', e);
        }
      }
      await this.renderApp();
    }
  }

  private async handleIframeInit(): Promise<void> {
    await this.bootstrapIframeSession();
    onIdTokenChanged(this.authService.firebaseAuth, (user) => this.handleIframeAuthChange(user));
  }

  private async handleIframeAuthChange(user: User | null): Promise<void> {
    Logger.logDebug('Firebase auth state changed', user);

    if (user) {
      const idToken = await user.getIdToken();
      await this.authService.refreshAGWLogin(idToken);
      this.messageService.sendUserInfo();
      return;
    }

    if (!this.authService.loggedUser && this.logoutInProgress) {
      Logger.logDebug('Logout is in progress. Skipping logout request...');
      return;
    }

    // Safari ITP guard: verify with the server before trusting a null event.
    if ((await this.bootstrapIframeSession()) !== SessionStatus.None) {
      return;
    }

    this.logoutInProgress = true;
    await this.authService.logout({ clearCookie: true });
    this.messageService.sendUserInfo();
  }

  /**
   * Establishes or re-establishes the iframe's Firebase session from the
   * server-side custom-token cookie. Clears the cookie only on invalid-token
   * errors — other failures return Unknown so callers do not mistake them
   * for proof of logout. Overlapping calls share a single in-flight run.
   */
  private bootstrapIframeSession(): Promise<SessionStatus> {
    this.bootstrapInFlight ??= this.executeIframeBootstrap().finally(() => {
      this.bootstrapInFlight = null;
    });
    return this.bootstrapInFlight;
  }

  private async executeIframeBootstrap(): Promise<SessionStatus> {
    if (this.authService.firebaseAuth.currentUser) {
      return SessionStatus.Active;
    }

    const customToken = await this.authService.getCustomToken().catch((error) => {
      Logger.logWarn('Failed to fetch custom token:', error);
      return null;
    });
    if (customToken === null) return SessionStatus.Unknown;
    if (!customToken.data) return SessionStatus.None;

    try {
      await this.authService.loginWithCustomToken(customToken.data);
      return SessionStatus.Active;
    } catch (error) {
      Logger.logError('Failed to sign in with custom token:', error);
      if (!UlaService.isInvalidCustomTokenError(error)) {
        return SessionStatus.Unknown;
      }
      await this.authService.clearCustomTokenCookie().catch((clearError) => {
        Logger.logError('Failed to clear stale custom token cookie:', clearError);
      });
      return SessionStatus.None;
    }
  }

  private static isInvalidCustomTokenError(error: unknown): boolean {
    const code = (error as { code?: unknown })?.code;
    return code === 'auth/invalid-custom-token' || code === 'auth/custom-token-mismatch';
  }
}
